import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Link, Check, Share2, MoreHorizontal, Settings, LogOut, Layers, Pin, PinOff } from 'lucide-react';
import { useCircle } from '../hooks/useCircle';
import { useAuth } from '../contexts/AuthContext';
import { leaveCircle, togglePinnedCircle } from '../services/circleService';
import InviteShareModal from '../components/InviteShareModal';
import SettingsDrawer from '../components/SettingsDrawer';

const ADMIN_UID = 'o2VVqpqGLvcPeXiNYe8FdGhXnBJ3';

export default function CirclesPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { circles, activeCircleId, selectCircle, pinnedCircles } = useCircle();
  const [shareCircle, setShareCircle] = useState(null);
  const [menuCircleId, setMenuCircleId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const menuRef = useRef(null);

  const togglePin = async (circleId) => {
    if (!currentUser?.uid) return;
    const isPinned = pinnedCircles.includes(circleId);
    await togglePinnedCircle(currentUser.uid, circleId, isPinned);
  };

  const sortedCircles = [...circles].sort((a, b) => {
    const aPinned = pinnedCircles.includes(a.id);
    const bPinned = pinnedCircles.includes(b.id);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    return 0;
  });

  useEffect(() => {
    if (!menuCircleId) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuCircleId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuCircleId]);

  const handleRowClick = (circle) => {
    selectCircle(circle.id);
    navigate('/art');
  };

  const handleLeave = async (circle) => {
    setMenuCircleId(null);
    if (window.confirm(`Leave "${circle.name}"? You can rejoin with the invite code.`)) {
      await leaveCircle(circle.id, currentUser.uid);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <SettingsButton onClick={() => setShowSettings(true)} title="Settings">
            <Settings size={20} />
          </SettingsButton>
          <Title>Circles</Title>
        </HeaderLeft>
        <HeaderButtons>
          {currentUser?.uid === ADMIN_UID && (
            <BulkButton onClick={() => navigate('/admin/bulk-circles')}>
              <Layers size={18} />
            </BulkButton>
          )}
          <AddButton onClick={() => navigate('/circles/new')}>
            <Plus size={20} />
          </AddButton>
        </HeaderButtons>
      </Header>

      {circles.length === 0 ? (
        <EmptyState>
          <EmptyEmoji>👨‍👩‍👧‍👦</EmptyEmoji>
          <EmptyTitle>No circles yet</EmptyTitle>
          <EmptyText>Create a circle and invite your family to start making art together.</EmptyText>
          <ButtonRow>
            <CreateButton onClick={() => navigate('/circles/new')}>
              <Plus size={18} />
              Create a Circle
            </CreateButton>
            <JoinButton onClick={() => navigate('/circles/new?mode=join')}>
              <Link size={18} />
              Join a Circle
            </JoinButton>
          </ButtonRow>
        </EmptyState>
      ) : (
        <CircleList>
          {sortedCircles.map((circle) => {
            const isActive = circle.id === activeCircleId;
            const isAdmin = circle.memberInfo?.[currentUser?.uid]?.role === 'admin';
            const isPinned = pinnedCircles.includes(circle.id);
            return (
              <CircleRow key={circle.id}>
                <RowBody onClick={() => handleRowClick(circle)}>
                  <CircleInfo>
                    <CircleName>
                      {isPinned && <PinIcon><Pin size={14} /></PinIcon>}
                      {circle.name}
                      {isActive && <ActiveBadge><Check size={12} /></ActiveBadge>}
                    </CircleName>
                    <CircleMeta>
                      {circle.memberIds?.length || 0} members
                    </CircleMeta>
                  </CircleInfo>
                </RowBody>

                <RowActions>
                  <ActionBtn
                    onClick={(e) => { e.stopPropagation(); togglePin(circle.id); }}
                    title={isPinned ? "Unpin" : "Pin to top"}
                  >
                    {isPinned ? <PinOff size={18} /> : <Pin size={18} />}
                  </ActionBtn>
                  <ActionBtn
                    onClick={(e) => { e.stopPropagation(); navigate(`/circles/${circle.id}`); }}
                    title="Settings"
                  >
                    <Settings size={18} />
                  </ActionBtn>
                  <ActionBtn
                    onClick={(e) => { e.stopPropagation(); setShareCircle(circle); }}
                    title="Share invite"
                  >
                    <Share2 size={18} />
                  </ActionBtn>
                  {!isAdmin && (
                    <MenuWrapper>
                      <ActionBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuCircleId(menuCircleId === circle.id ? null : circle.id);
                        }}
                        title="More options"
                      >
                        <MoreHorizontal size={18} />
                      </ActionBtn>
                      {menuCircleId === circle.id && (
                        <DropdownMenu ref={menuRef}>
                          <MenuItem $danger onClick={() => handleLeave(circle)}>
                            <LogOut size={16} />
                            Leave Circle
                          </MenuItem>
                        </DropdownMenu>
                      )}
                    </MenuWrapper>
                  )}
                </RowActions>
              </CircleRow>
            );
          })}
        </CircleList>
      )}

      <InviteShareModal
        isOpen={!!shareCircle}
        onClose={() => setShareCircle(null)}
        inviteCode={shareCircle?.inviteCode}
        circleName={shareCircle?.name}
      />

      <SettingsDrawer
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentCircle={null}
      />
    </Container>
  );
}

const Container = styled.div`
  padding: 0 16px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  gap: 12px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const SettingsButton = styled.button`
  padding: 10px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const BulkButton = styled.button`
  padding: 10px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const AddButton = styled.button`
  padding: 10px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.accent};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const CircleList = styled.div`
  display: flex;
  flex-direction: column;
`;

const CircleRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};
`;

const RowBody = styled.button`
  flex: 1;
  min-width: 0;
  text-align: left;
  padding: 4px 0;
  border-radius: ${({ theme }) => theme.radius.medium};
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    margin: 0 -8px;
    padding: 4px 8px;
  }
`;

const CircleInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CircleName = styled.div`
  font-size: 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PinIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.accent};
  flex-shrink: 0;
`;

const ActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  color: white;
`;

const CircleMeta = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-left: 8px;
`;

const ActionBtn = styled.button`
  padding: 10px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 160px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  z-index: 50;
  overflow: hidden;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  font-size: 16px;
  font-weight: 500;
  color: ${({ $danger, theme }) => $danger ? theme.colors.error : theme.colors.textPrimary};
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 24px;
`;

const EmptyEmoji = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 24px;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 17px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);

  &:hover {
    transform: translateY(-1px);
  }
`;

const JoinButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: transparent;
  border: 1.5px solid ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.accent};
  font-size: 17px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.accent}10;
    transform: translateY(-1px);
  }
`;
